import { ALL_EMOJIS } from "@/lib/emojiSets";
import React, { useLayoutEffect, useRef } from "react";
import { MapPin } from "lucide-react";
import { storyFontStyle } from "@/lib/storyFonts";
import { resolveMediaUrl, captureVideoPosterFromUrl } from "@/components/FileUpload";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";
import { stripEmbedUrl, embedLabel } from "@/lib/linkText";
import { fxStyle } from "@/lib/mediaFx";
import { openExternal } from "@/lib/externalLinks";
import { useGuard } from "@/lib/guards";
import { fitFontSize } from "@/lib/useAutoFitText";

// Lot 713 — zone sûre du cadre en FRACTIONS de sa hauteur (en-tête nom/photo ≈ 12 %, barre « Répondre » + réactions
// ≈ 17 % sur un téléphone) : la même règle vaut pour la visionneuse, l'éditeur et la vignette (invariante à l'échelle).
export const SAFE_TOP_FR = 0.12;
export const SAFE_BOTTOM_FR = 0.17;
export const BUBBLE_MAX_CHARS = 300;     // bulle libre (outil « Aa »)
export const LONG_TEXT_MAX_CHARS = 600;  // « Texte long » : centré, taille ajustée au cadre
export const LONG_TEXT_RATIO = 0.11;     // taille maximale d'un texte long = 11 % de la largeur du cadre

// Lot 713 — ajuste UNE bulle de texte au cadre : si elle dépasse la zone sûre (hauteur ou largeur), sa taille est réduite
// (recherche dichotomique sur la mise en page réelle) ; puis son centre est ramené entre l'en-tête et la barre du bas
// (variable CSS --fit-dy lue par stickerWrapStyle). Un « texte long » est toujours centré et prend la place disponible.
export const fitBubble = (el, s, baseSize = `${(s.scale || 0.22) * 100}cqw`) => {
  const wrap = el.parentElement;
  const frame = el.closest("[data-story-frame]");
  el.style.fontSize = baseSize; // retour à la taille d'auteur avant de mesurer
  wrap?.style.setProperty("--fit-dy", "0px");
  if (!frame) return null;
  const W = frame.clientWidth, H = frame.clientHeight;
  if (!W || !H) return null;
  const top = (Number(frame.dataset.safeTop) || 0) * H;
  const bottom = (Number(frame.dataset.safeBottom) || 0) * H;
  const zone = Math.max(24, H - top - bottom);
  const maxH = zone * 0.94, maxW = W * 0.92;
  const basePx = (s.long ? LONG_TEXT_RATIO : (s.scale || 0.22)) * W;
  const over = el.offsetHeight > maxH + 1 || el.offsetWidth > maxW + 1;
  let px = null;
  if (s.long || over) {
    px = fitFontSize(el, { minPx: Math.max(5, W * 0.025), maxPx: basePx, maxW, maxH });
    el.style.fontSize = `${px}px`;
  }
  const h = el.offsetHeight;
  const yPx = (s.long ? 0.5 : (s.y ?? 0.5)) * H;
  const lo = top + h / 2 + 2, hi = H - bottom - h / 2 - 2;
  const center = s.long || lo > hi ? (top + (H - bottom)) / 2 : Math.min(Math.max(yPx, lo), hi);
  const dy = Math.round(center - yPx);
  if (dy) wrap?.style.setProperty("--fit-dy", `${dy}px`);
  return { px, dy };
};

// Bulle de texte (visionneuse, éditeur, vignette) : même style partout ; ajustée au cadre quand le garde-fou
// « story_bubble_fit » est actif (coupé → taille et place telles que choisies par l'auteur, comme avant la 2.4.3).
const FittedText = ({ s, body, interactive }) => {
  const ref = useRef(null);
  const fitOn = useGuard("story_bubble_fit");
  const size = `${(s.scale || 0.22) * 100}cqw`;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (!fitOn) { el.style.fontSize = size; el.parentElement?.style.setProperty("--fit-dy", "0px"); return undefined; }
    fitBubble(el, s, size);
    let alive = true; // police Google arrivée après coup → métriques changées → on ré-ajuste une fois
    try { document.fonts?.ready?.then(() => { if (alive && ref.current) fitBubble(ref.current, s, size); }); } catch { /* noop */ }
    return () => { alive = false; };
  }, [s, fitOn, body, size]);
  return (
    <span ref={ref} className={interactive && body !== s.text ? "pointer-events-auto" : undefined} style={{ ...textStickerStyle(s, size), maxWidth: "92cqw" }} data-long={s.long ? "1" : undefined} data-testid={`sticker-text-${s.id}`}>
      {body}
    </span>
  );
};

// Lot 664 — URL dans une bulle de texte (aperçu de lien absent, ex. Facebook) → lien tapable dans la visionneuse.
const STICKER_URL_RE = /(https?:\/\/[^\s]+)/gi;
const linkifyStickerText = (text) => {
  const out = [];
  let last = 0, m, i = 0;
  STICKER_URL_RE.lastIndex = 0;
  while ((m = STICKER_URL_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const href = m[0];
    out.push(<a key={`u${i++}`} href={href} className="underline break-all" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openExternal(href).catch(() => {}); }} onPointerDown={(e) => e.stopPropagation()} data-testid="story-sticker-link">{href}</a>);
    last = m.index + href.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

// Sticker « lien » (carte d'aperçu déplaçable, façon Instagram) : taille par défaut et bornes (fraction de la largeur du cadre).
export const LINK_STICKER_DEFAULT = { x: 0.5, y: 0.66, scale: 0.8, rotation: 0 };
export const LINK_STICKER_SCALE = { min: 0.35, max: 0.95 };
// Lot 664 — zone sûre de la visionneuse (StoryBar) : en-tête nom/photo · barre « Répondre » + réactions.
export const VIEWER_SAFE_TOP = "4.5rem";
export const VIEWER_SAFE_BOTTOM = "8rem";
export const hasLinkSticker = (stickers) => Array.isArray(stickers) && stickers.some((s) => s?.type === "link");

// Big-emoji sticker palette (grief, birth, love, celebration…).
export const STICKER_EMOJIS = ALL_EMOJIS; // lot 634 — 3 catégories (lib/emojiSets)

// Christian-themed sticker pack (Yalola signature).
export const CHRISTIAN_EMOJIS = ["✝️", "🕊️", "⛪", "📖", "🕯️", "😇", "👼", "🤲", "🛐", "🙏", "📿", "🔔"];

// Colors for text stickers.
export const STICKER_COLORS = ["#FFFFFF", "#111111", "#F2C46D", "#E11D48", "#3A7BD5", "#22C55E", "#B24592"];

// Predefined Christian text badges (announcements: death, birth, praise…). `key` = Admin › Traductions
// (story.badge_*), `text` = French default.
export const TEXT_BADGES = [
  { key: "story.badge_rip", text: "R.I.P.", color: "#FFFFFF", bg: "#111111" },
  { key: "story.badge_rest_in_peace", text: "Repose en paix 🕊️", color: "#FFFFFF", bg: "rgba(0,0,0,0.55)" },
  { key: "story.badge_newborn", text: "Nouveau-né 👶", color: "#111111", bg: "#F2C46D" },
  { key: "story.badge_glory", text: "Gloire à Dieu 🙌", color: "#FFFFFF", bg: "#B24592" },
  { key: "story.badge_amen", text: "Amen 🙏", color: "#FFFFFF", bg: "#3A7BD5" },
  { key: "story.badge_congrats", text: "Félicitations 🎉", color: "#111111", bg: "#22C55E" },
];

// Non-destructive pan/zoom applied to story media at display time.
export const mediaTransformStyle = (tf) => {
  if (!tf) return undefined;
  const base = `translate(${tf.x || 0}%, ${tf.y || 0}%) scale(${tf.scale || 1})`;
  // Lot 534 : filtre + miroir (vidéo) stockés dans media_transform.fx — même rendu éditeur / aperçu / viewer / vignette.
  return { ...(fxStyle(tf.fx, base) || { transform: base }), transformOrigin: "center" };
};

// Fond flou derrière un média `object-contain` (à la place des bandes noires) — même recette que le lecteur vidéo du fil.
// Vidéo : passer l'affiche ; sans affiche (poster neutre) → rien.
// Source du fond flou d'un média en cours de création : photo = la photo ; vidéo = affiche locale
// (capturée au choix du fichier) ou affiche déjà en ligne ; sinon null (pas de flou).
export const storyBlurSrc = (media, mediaSrc) => {
  if (!media) return null;
  if (media.media_type !== "video") return mediaSrc;
  return media.posterLocal || (media.poster_url ? resolveMediaUrl(media.poster_url) : null);
};

// Capture l'affiche d'une vidéo locale → `posterLocal` (blob URL) sur le média, si c'est toujours le même fichier.
export const attachLocalPoster = (localUrl, file, setMedia) => {
  captureVideoPosterFromUrl(localUrl).then((blob) => {
    if (!blob) return;
    const posterLocal = URL.createObjectURL(blob);
    setMedia((m) => {
      if (!m || m.file !== file) { URL.revokeObjectURL(posterLocal); return m; }
      return { ...m, posterLocal };
    });
  }).catch(() => {});
};

export const revokeMediaUrls = (m) => {
  if (m?.localUrl) URL.revokeObjectURL(m.localUrl);
  if (m?.posterLocal) URL.revokeObjectURL(m.posterLocal);
};

export const StoryBlurBackdrop = ({ src, testId }) => {
  if (!src || src === NEUTRAL_POSTER) return null;
  return (
    <>
      <img src={src} alt="" aria-hidden draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none" style={{ filter: "blur(28px) brightness(0.9)", transform: "scale(1.18)" }} data-testid={testId} />
      {/* Voile haut/bas : en-tête, barre de progression et réponses restent lisibles sur un flou clair. */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 24%, rgba(0,0,0,0) 74%, rgba(0,0,0,0.5) 100%)" }} />
    </>
  );
};

// Position + rotation d'un sticker dans le cadre (x/y = fractions, centre du sticker).
// `width: max-content` : la boîte n'est pas rognée par la place restante à droite du point d'ancrage
// (left: 50 % → sans cela le texte se replierait sur la moitié du cadre).
export const stickerWrapStyle = (s) => ({
  left: `${(s.x ?? 0.5) * 100}%`,
  top: `${(s.y ?? 0.5) * 100}%`,
  width: "max-content",
  maxWidth: "94%",
  // Lot 713 — --fit-dy : recentrage vertical posé par fitBubble quand la bulle déborderait de la zone sûre.
  transform: `translate(-50%, calc(-50% + var(--fit-dy, 0px))) rotate(${s.rotation || 0}deg)`,
});

// Style d'une bulle de texte (police, alignement, pastille). `fontSize` = taille CSS (cqw dans le
// cadre, px dans l'outil texte) — même rendu dans l'éditeur, la visionneuse et la vignette.
export const textStickerStyle = (s, fontSize) => ({
  display: "inline-block",
  fontSize,
  lineHeight: 1.2,
  fontWeight: 700,
  color: s.color || "#fff",
  background: s.bg || "transparent",
  padding: s.bg ? "0.2em 0.5em" : 0,
  borderRadius: s.bg ? "0.4em" : 0,
  textAlign: s.align || "center",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  textShadow: s.bg ? "none" : "0 2px 6px rgba(0,0,0,0.5)",
  ...storyFontStyle(s.font),
});

const shadow = { textShadow: "0 2px 10px rgba(0,0,0,0.45)" };

// Carte de lien en sticker : largeur = scale × cadre, textes proportionnels (cqw) → même rendu dans l'éditeur,
// la visionneuse et la vignette. `interactive` (visionneuse) : un tap ouvre le lien.
export const LinkStickerCard = ({ embed, scale = 0.8, interactive = false, testId }) => {
  if (!embed?.url) return null;
  const w = (scale || 0.8) * 100;
  const fs = (k) => `${(w * k).toFixed(2)}cqw`;
  const style = { width: `${w}cqw`, background: "rgba(0,0,0,0.55)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff", backdropFilter: "blur(6px)", borderRadius: fs(0.05), boxShadow: "0 8px 24px rgba(0,0,0,0.3)" };
  const inner = (
    <>
      {embed.thumbnail && <img src={embed.thumbnail} alt="" loading="lazy" decoding="async" draggable={false} onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full aspect-video object-cover pointer-events-none" />}
      <div className="text-left" style={{ padding: `${fs(0.03)} ${fs(0.04)}` }}>
        <div className="font-semibold uppercase tracking-wide truncate" style={{ fontSize: fs(0.028), color: "rgba(255,255,255,0.65)" }}>{embedLabel(embed)}</div>
        <div className="font-semibold leading-snug line-clamp-2" style={{ fontSize: fs(0.042), overflowWrap: "anywhere" }} data-testid={testId ? `${testId}-title` : undefined}>{embed.title || embed.url}</div>
        {embed.author && <div className="truncate" style={{ fontSize: fs(0.032), color: "rgba(255,255,255,0.65)", marginTop: fs(0.006) }}>{embed.author}</div>}
      </div>
    </>
  );
  if (!interactive) return <div className="overflow-hidden" style={style} data-testid={testId}>{inner}</div>;
  return (
    <a href={embed.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="block overflow-hidden transition-opacity hover:opacity-95" style={style} data-testid={testId}>
      {inner}
    </a>
  );
};

// Contenu d'un sticker (sans position) : emoji, bulle de texte, verset, heure/date, lieu, @mention, carte de lien.
export const StickerBody = ({ s, embed = null, interactive = false }) => {
  const size = `${(s.scale || 0.22) * 100}cqw`;
  if (s.type === "link") {
    return <LinkStickerCard embed={embed} scale={s.scale} interactive={interactive} testId={`sticker-link-${s.id}`} />;
  }
  if (s.type === "text") {
    // lot 664 : URL restée dans la bulle (aperçu absent) → tapable dans la visionneuse ; lot 713 : bulle ajustée au cadre
    const body = interactive && /https?:\/\//i.test(s.text || "") ? linkifyStickerText(s.text) : s.text;
    return <FittedText s={s} body={body} interactive={interactive} />;
  }
  if (s.type === "verse") {
    return (
      <div
        className="text-left rounded-[1em] px-[1em] py-[0.9em]"
        style={{ fontSize: size, width: "78cqw", background: "rgba(15,23,42,0.74)", border: "1px solid rgba(253,224,71,0.45)", backdropFilter: "blur(8px)", boxShadow: "0 8px 30px rgba(0,0,0,0.35)" }}
      >
        <div className="leading-snug" style={{ color: "#FEF3C7", fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic" }}>« {s.text} »</div>
        {s.ref && <div className="mt-[0.5em] font-mono uppercase tracking-widest" style={{ fontSize: "0.6em", color: "rgba(253,224,71,0.9)" }}>📖 {s.ref}</div>}
      </div>
    );
  }
  if (s.type === "datetime") {
    return (
      <div className="text-center" style={{ fontSize: size }}>
        <div className="font-bold tabular-nums leading-none text-white" style={{ ...shadow, letterSpacing: "-0.02em" }}>{s.text}</div>
        {s.sub && <div className="mt-[0.2em] font-semibold uppercase tracking-wider text-white/90" style={{ ...shadow, fontSize: "0.3em", whiteSpace: "nowrap" }}>{s.sub}</div>}
      </div>
    );
  }
  if (s.type === "location") {
    return (
      <span className="inline-flex items-center gap-[0.35em] rounded-full font-bold px-[0.8em] py-[0.35em]" style={{ fontSize: size, background: "rgba(255,255,255,0.92)", color: "#0f172a", boxShadow: "0 6px 18px rgba(0,0,0,0.3)", whiteSpace: "nowrap" }}>
        <MapPin size="1em" style={{ color: "#F97316" }} /> {s.text}
      </span>
    );
  }
  if (s.type === "image") {
    return <img src={resolveMediaUrl(s.url)} alt="" draggable={false} className="rounded-2xl object-cover pointer-events-none" style={{ width: size, height: size, boxShadow: "0 8px 24px rgba(0,0,0,0.35)", background: "rgba(255,255,255,0.9)" }} />;
  }
  if (s.type === "mention") {
    return (
      <span className="inline-block rounded-full font-bold px-[0.8em] py-[0.35em] text-white" style={{ fontSize: size, background: "linear-gradient(90deg,#F97316,#EC4899)", boxShadow: "0 6px 18px rgba(0,0,0,0.3)", whiteSpace: "nowrap" }}>
        {s.text}
      </span>
    );
  }
  return <span style={{ fontSize: size, lineHeight: 1, filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.4))" }}>{s.emoji}</span>;
};

// Stickers à afficher : sans l'URL déjà portée par la carte de lien (bulle vidée → retirée) ; le sticker « lien »
// n'existe que si la story porte une carte.
export const visibleStickers = (stickers, embed) => {
  if (!stickers?.length) return [];
  if (!embed?.url) return stickers.filter((s) => s.type !== "link");
  return stickers
    .map((s) => (s.type === "text" ? { ...s, text: stripEmbedUrl(s.text, embed) } : s))
    .filter((s) => s.type !== "text" || s.text);
};

// Read-only sticker overlay. Sizes scale with the container width (cqw) so it looks right
// on both the small tile and the full-screen viewer. `interactive` : la carte de lien s'ouvre au tap (visionneuse).
// `safeInset` (lot 664) : anciennes stories texte publiées avant la zone sûre → le calque est réduit à la zone visible
// (sous l'en-tête, au-dessus de la barre « Répondre ») et les positions fractionnaires s'y remappent.
export const StoryStickers = ({ stickers, embed, interactive = false, safeInset = null, testId }) => {
  const shown = visibleStickers(stickers, embed);
  if (!shown.length) return null;
  return (
    <div
      className={`absolute pointer-events-none overflow-hidden ${safeInset ? "inset-x-0" : "inset-0"}`}
      style={{ containerType: "inline-size", ...(safeInset ? { top: safeInset.top, bottom: safeInset.bottom } : {}) }}
      data-testid={testId}
      data-safe-inset={safeInset ? "1" : undefined}
      data-story-frame="1"
      data-safe-top={safeInset ? 0 : SAFE_TOP_FR}
      data-safe-bottom={safeInset ? 0 : SAFE_BOTTOM_FR}
    >
      {shown.map((s) => (
        <span key={s.id} className={`absolute select-none ${s.type === "link" && interactive ? "pointer-events-auto z-10" : ""}`} style={stickerWrapStyle(s)}>
          <StickerBody s={s} embed={embed} interactive={interactive} />
        </span>
      ))}
    </div>
  );
};

// Calque de dessin (PNG transparent) étiré sur tout le cadre — mêmes proportions que les
// positions fractionnaires des stickers, donc aligné avec ce que l'auteur a dessiné.
export const StoryDrawing = ({ url, testId }) => {
  if (!url) return null;
  return <img src={resolveMediaUrl(url)} alt="" draggable={false} className="absolute inset-0 w-full h-full pointer-events-none select-none" style={{ objectFit: "fill" }} data-testid={testId} />;
};
